/**
 * Planning roles of a block (spec §8.11, §14.6.1, M18): `km:role` links to one of the punned
 * `km:PlanningRole` classes. A block may carry several (Q49); the projector also asserts each as an
 * `rdf:type` of the block (§13.3), which is what makes "this block is a position" a checkable claim
 * rather than a note about a topic (§14.7 rule 5).
 */
import type { LoroDoc, TreeID } from "loro-crdt";
import { km } from "../lib/iri";
import { addBlockLink, readLinks, removeBlockLink, type LinkEntry } from "./model";

export const ROLE_PREDICATE = km("role");
export const PLANNING_ROLE = km("PlanningRole");

export interface BlockRole {
  /** the link that states the role, so it can be removed again */
  linkId: string;
  iri: string;
}

export function readRoles(doc: LoroDoc, docId: string, treeId: TreeID): BlockRole[] {
  return readLinks(doc, docId, treeId)
    .filter((l: LinkEntry) => l.p === ROLE_PREDICATE && l.o.kind !== "literal" && l.o.value)
    .map((l) => ({ linkId: l.id, iri: l.o.value }));
}

/** Add a role the block does not have yet; returns the link id, or null when it already has it. */
export function addRole(doc: LoroDoc, docId: string, treeId: TreeID, roleIri: string, userId: string): string | null {
  if (readRoles(doc, docId, treeId).some((r) => r.iri === roleIri)) return null;
  return addBlockLink(doc, treeId, ROLE_PREDICATE, { kind: "iri", value: roleIri }, userId);
}

export function removeRole(doc: LoroDoc, treeId: TreeID, linkId: string): void {
  removeBlockLink(doc, treeId, linkId);
}

export const sameRoles = (a: BlockRole[], b: BlockRole[]): boolean =>
  a.length === b.length && a.every((r, i) => r.linkId === b[i]!.linkId && r.iri === b[i]!.iri);
